import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
} from "@mui/material";
import { Button, Toggle } from "pepsico-ds";
import { useContext, useEffect, useState } from "react";
import LoaderContext from "../../context/LoaderContext";
import { SurveyContext } from "../../context/SurveyContext";
import { useSurveyHandlerHook } from "../../hooks/useSurveyHandlerHook";
import { FeatureToggle, useFeatureToggle } from "../../utils/featureToggle";
import LogNoteModel from "../common/LogNoteModel/LogNoteModel";
import JsonFormsWrapper from "../jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import "./survey.scss";
import {
  createSurveyJsonSchema,
  createSurveyUiSchema,
} from "./surveyComponentMocks";

function CreateSurveyModal({
  createSurveyModalOpen,
  toggleCreateSurveyModal,
  actionStatus,
}) {
  const {
    surveyState,
    setSurveyState,
    initialState,
    load,
    handleSaveSurvey,
    errMsg,
    validateSurvey,
  } = useContext(SurveyContext);
  const { handleSurvey } = useSurveyHandlerHook();
  const { setIsLoading } = useContext(LoaderContext);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [flag, setFlag] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const isLogNoteModalEnabled = useFeatureToggle(
    FeatureToggle.isLogNoteModalEnabled
  );
  const [showLogNoteModal, setShowLogNoteModal] = useState(false);

  const isEditing = actionStatus === "edit";
  const isActive =
    surveyState?.status === "ACTIVE" || surveyState?.status === "INACTIVE";

  const handleCreate = async () => {
    if (!(await validateSurvey())) return;
    setIsLoading(true);
    setFlag(true);
    const result = await handleSaveSurvey();
    setIsLoading(false);
    setFlag(false);

    if (result && !result.success) {
      setSnackbarMessage(result.message || "An error occurred while saving.");
      setSnackbarOpen(true);
      return;
    }

    toggleCreateSurveyModal();
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleIsActive = (e) => {
    const tempState = { ...surveyState };
    tempState["paused"] = !e;
    handleSurvey(tempState);
  };

  useEffect(() => {
    setIsLoading(load);
  }, [load, setIsLoading]);

  useEffect(() => {
    if (errMsg?.length > 0) {
      setSnackbarMessage(errMsg.join(", "));
      setSnackbarOpen(true);
    }
  }, [errMsg]);

  return (
    <>
      <Dialog
        className="survey-modal"
        open={createSurveyModalOpen}
        scroll="paper"
        sx={{ "& .MuiPaper-root": { width: "70%" } }}
      >
        <DialogTitle>
          <Grid container justifyContent="space-between" direction="row">
            <Grid item xs={10}>
              <h2 style={{ marginLeft: "18%", textAlign: "center" }}>
                {actionStatus && actionStatus === "edit"
                  ? "Edit Survey/Quiz"
                  : "Create New Survey/Quiz"}
              </h2>
            </Grid>
            <Grid item xs={1} display="flex" justifyContent="flex-end">
              <CloseIcon
                data-testid="CloseIcon"
                onClick={() => {
                  setSurveyState(initialState);
                  toggleCreateSurveyModal();
                }}
              />
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent sx={{ overflowX: "hidden" }}>
          <div className="survey-mod">
            <h4 style={{ textAlign: "right", width: "100%" }}>*Required fields</h4>
            <JsonFormsWrapper
              jsonschema={createSurveyJsonSchema}
              uischema={createSurveyUiSchema(isEditing, isActive, actionStatus)}
              data={surveyState}
              onChange={(data) => handleSurvey(data)}
            />
          </div>
          <div className="bottom-section">
            {isActive && (
              <Toggle
                label="Active"
                onUpdate={handleIsActive}
                size="medium"
                checked={
                  surveyState?.status?.toLowerCase() === "active" ? true : false
                }
              />
            )}
            <div className="bottom-button">
              <Button
                onClick={() => {
                  if (
                    isLogNoteModalEnabled &&
                    actionStatus &&
                    actionStatus === "edit"
                  ) {
                    setShowLogNoteModal(true);
                  } else {
                    handleCreate();
                  }
                }}
                disabled={flag}
                data-testid="create-survey-button"
              >
                {actionStatus && actionStatus === "edit" ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
        {isLogNoteModalEnabled && (
          <LogNoteModel
            showLogNoteModal={showLogNoteModal}
            setShowLogNoteModal={setShowLogNoteModal}
            onChange={(note, changeMade) => {
              setSurveyState({
                ...surveyState,
                note,
                changeMade,
              });
            }}
            onSubmit={(data) => {
              // Update survey state with the submitted note and changeMade
              setSurveyState({
                ...surveyState,
                note: data.note,
                changeMade: data.changeMade,
              });
              setShowLogNoteModal(false);
              handleCreate();
            }}
          />
        )}
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default CreateSurveyModal;
